import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { entityDetailKey } from "@/lib/queryKeys";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { dealDetailLoadError } from "./dealDetailLoadError";
import { UserNotesPanel } from "@/components/notes/UserNotesPanel";
import { DealBrandsPanel } from "@/components/deals/DealBrandsPanel";
import { DealParticipantsPanel } from "@/components/deals/DealParticipantsPanel";
import { DealMeetingsPanel } from "@/components/deals/DealMeetingsPanel";

interface DealDrawerContentProps {
  id: number;
  onOpenFullDealModal?: (deal: any) => void;
}

function formatCurrency(value: unknown): string {
  const num = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  if (!Number.isFinite(num)) return "-";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * DealDrawerContent
 * Anteprima rapida di un'opportunita' nel RecordDrawer.
 * Dati da /api/deals/:id.
 */
export default function DealDrawerContent({ id, onOpenFullDealModal }: DealDrawerContentProps) {
  const [, navigate] = useLocation();

  const { data: deal, isLoading, isError, error, refetch } = useQuery({
    queryKey: entityDetailKey('deals', id),
    queryFn: () => apiRequest(`/api/deals/${id}`),
    enabled: id != null,
  });

  if (isLoading) {
    return <div className="p-5 text-muted-foreground">Caricamento...</div>;
  }

  if (isError || !deal) {
    return (
      <div role="alert" className="p-5 space-y-3 text-destructive">
        <p>{dealDetailLoadError(error)}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
          Riprova
        </Button>
      </div>
    );
  }

  const stageName: string | undefined = deal.stage?.name || deal.stageName;
  const companyId: number | undefined = deal.company?.id || deal.companyId;
  const companyName: string | undefined = deal.company?.name || deal.companyName;
  const contactId: number | undefined = deal.contact?.id || deal.contactId;
  const contactName: string | undefined =
    deal.contact ? [deal.contact.firstName, deal.contact.lastName].filter(Boolean).join(" ") : deal.contactName;
  const branchId: number | undefined = deal.branch?.id || deal.branchId;
  const branchName: string | undefined = deal.branch?.name || deal.branchName;
  const expectedClose: string | undefined = deal.expectedCloseDate || deal.expectedClose || deal.closeDate;

  return (
    <div className="flex flex-col">
      <h2 className="text-lg font-semibold p-5 border-b border-border text-foreground">
        {deal.name || deal.title || "Opportunita' senza nome"}
      </h2>

      <div className="p-5 border-b border-border">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Valore</div>
        <div className="text-foreground text-base font-medium">{formatCurrency(deal.dealValue ?? deal.value)}</div>
      </div>

      {stageName && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Fase</div>
          <Badge variant="secondary" className="text-xs">{stageName}</Badge>
        </div>
      )}

      {companyName && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Azienda</div>
          {companyId ? (
            <button
              type="button"
              onClick={() => navigate(`/companies/${companyId}`)}
              className="text-primary hover:underline"
            >
              {companyName}
            </button>
          ) : (
            <span className="text-foreground">{companyName}</span>
          )}
        </div>
      )}

      <div className="p-5 border-b border-border"><DealBrandsPanel dealId={id} /></div>

      {contactName && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Contatto</div>
          {contactId ? (
            <button
              type="button"
              onClick={() => navigate(`/contacts/${contactId}`)}
              className="text-primary hover:underline"
            >
              {contactName}
            </button>
          ) : (
            <span className="text-foreground">{contactName}</span>
          )}
        </div>
      )}

      <div className="p-5 border-b border-border">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Partecipanti</div>
        <DealParticipantsPanel
          dealId={id}
          primaryCompany={companyId && companyName ? { id: companyId, name: companyName } : null}
          primaryContact={contactId && contactName ? { id: contactId, name: contactName, companyName } : null}
        />
      </div>

      <div className="p-5 border-b border-border">
        <DealMeetingsPanel dealId={id} />
      </div>

      {branchName && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Filiale</div>
          {branchId ? (
            <button type="button" onClick={() => navigate(`/branches/${branchId}`)} className="text-primary hover:underline">
              {branchName}
            </button>
          ) : <span className="text-foreground">{branchName}</span>}
        </div>
      )}

      {expectedClose && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Chiusura prevista</div>
          <div className="text-foreground">
            {new Date(expectedClose).toLocaleDateString("it-IT")}
          </div>
        </div>
      )}

      <div className="p-5 border-b border-border">
        <UserNotesPanel entityType="deal" entityId={id} notesUser={deal.notes_user || deal.notesUser} />
      </div>

      <div className="p-5">
        <button
          type="button"
          onClick={() => onOpenFullDealModal ? onOpenFullDealModal(deal) : navigate(`/deals/${id}`)}
          className="text-sm text-primary hover:underline"
        >
          {onOpenFullDealModal ? 'Apri modale completo' : 'Apri scheda completa'}
        </button>
      </div>
    </div>
  );
}
